import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@economic/taco';
import { redirectToLegacyPage, refreshCurrentLegacyPage, useApplicationContext, useCurrentUser } from '@economic/react-app';
import useQuery from '../../../../shared/hooks/useQuery';
import {
    SendPurchaseOrderInformation,
    SendPurchaseOrderRequest,
    SendPurchaseOrderResponse,
} from '../../../../../types/internal-api';
import { t, useLocalization } from '../../../../shared/components/l10n/l10n';
import { SendPurchaseOrderFormData } from './types';
import { SendPurchaseOrderDialog } from './SendPurchaseOrderDialog';
import { splitEmails } from '../../../sales/invoicing/invoices/utils';
import internalApiClient from '../../../../shared/util/api/internalApiClient';
import { SendByType } from '../../../sales/invoicing/invoices/book-and-send/types';
import { useEmailTemplates } from '../../../sales/invoicing/invoices/utils/useEmailTemplates';
import { InvoiceLayoutType } from '../../../../../types/internal-api-extra';
import { emailOptionsFromTemplates } from '../../../sales/invoicing/invoices/utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../../sales/invoicing/invoices/utils/emailTemplateFormState';

function SendPurchaseOrderDialogContainer() {
    const toast = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const { state = {} } = location;
    const { purchaseOrderId, sendFromArchive } = state as any;

    const { loading: loadingInformation, data } = useQuery<SendPurchaseOrderInformation>(
        `/inventory/purchaseorder/emailinformation?purchaseOrderId=${purchaseOrderId}`,
        null,
        { skip: !purchaseOrderId }
    );

    const loadingTexts = useLocalization(textIds);
    const { data: templates, loading: loadingTemplates } = useEmailTemplates(InvoiceLayoutType.PurchaseOrder);
    const [sending, setSending] = useState<boolean>(false);

    const {
        configuration: { routes },
    } = useApplicationContext();
    const user = useCurrentUser();

    const transformedInformation = useMemo<SendPurchaseOrderFormData | undefined>(() => {
        if (!data) {
            return undefined;
        }

        return {
            isSent: data.isSent,
            emailOptions: {
                ...data,
                recipients: data.recipients.join(' '),
                recipientsCc: data.recipientsCc.join(' '),
                recipientsBcc: '',
                saveSubject: false,
                saveBody: false,
                showPdf: data.showAfterSending,
                ...emailOptionsFromTemplates(templates),
            },
            ...emailTemplateFormState(templates),
        };
    }, [data, templates]);

    async function send(values: SendPurchaseOrderFormData) {
        setSending(true);

        const requestData: SendPurchaseOrderRequest = {
            sendEmail: values.sendByType === SendByType.email,
            purchaseOrderId,
            ...values.emailOptions,
            showAfterSending: values.emailOptions.showPdf,
            recipients: splitEmails(values.emailOptions.recipients),
            recipientsCc: splitEmails(values.emailOptions.recipientsCc),
            recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
        };

        const result = await internalApiClient.post<SendPurchaseOrderResponse>(`/inventory/purchaseorder/Send`, requestData);

        setSending(false);
        toast.success(t`TOAST_PURCHASE_ORDER_SEND_SUCCESS`);

        if (values.emailOptions.showPdf && result.data.pdfLink) {
            window.top && window.top.firebirdTask.then(() => window.top?.PopUp2(result.data.pdfLink, 500, 600));
        }

        handleRedirect();
        hide();
    }

    function handleRedirect() {
        const prefix = user.isBasicUser ? '/basic' : '';
        if (sendFromArchive) {
            refreshCurrentLegacyPage('content');
        } else {
            redirectToLegacyPage(`${process.env.ECONOMIC_PATH}/${routes[`${prefix}/inventory/purchases/orders`]}`, 'content');
        }
    }

    function hide() {
        navigate(location.pathname);
    }

    if (!purchaseOrderId) {
        return null;
    }

    return (
        <SendPurchaseOrderDialog
            open={location.hash === '#send-purchase-order'}
            loading={loadingInformation || loadingTemplates || loadingTexts || sending}
            information={transformedInformation}
            onSend={send}
            onHide={hide}
        />
    );
}

const textIds = [
    'NOTIFICATION_LOADING',
    'SHARED_FORM_OK',
    'FORM_BOOKANDSEND_PURCHASEORDER_ERRORS_HEADER_CANNOTSEND',
    'FORM_BOOKANDSEND_PURCHASEORDER_HEADER',
    'FORM_BOOKANDSEND_PURCHASEORDER_SENDBYEMAIL',
    'FORM_BOOKANDSEND_PURCHASEORDER_REGISTERASSENT',
    'FORM_BOOKANDSEND_PURCHASEORDER_SEND',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'POPUP_INVOICEETC_ERROR_INVALIDEMAIL',
    'FORM_BOOKANDSEND_PURCHASEORDER_SHOWAFTERSENDING',
    'TOAST_PURCHASE_ORDER_SEND_SUCCESS',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
];

export default SendPurchaseOrderDialogContainer;
